// Fichier maintenu à la main, PAS généré : aucun script `supabase gen types`
// ne le régénère (vérifié dans `package.json`). Trois câblages ne doivent
// jamais disparaître d'une édition : l'option `global: { fetch: guardedFetch }`
// (la porte unique, voir plus bas), l'adaptateur `auth.storage: hybridAuthStorage`
// et la signature de version `withClientSignature` autour du `baseFetch`.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { hybridAuthStorage } from './authStorage';
import { isSessionExpected, isLogoutInProgress } from '@/lib/auth/sessionExpectation';
import { isAnonymousDataPath } from '@/lib/auth/anonymousDataRoutes';
import {
  createSessionGuardedFetch,
  runRegisteredRefresher,
} from '@/lib/auth/sessionGuardedFetch';
import { withClientSignature } from '@/lib/clientVersion/signature';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

// La porte unique : aucune requête `/rest/v1/` ni `/functions/v1/` ne part en
// rôle `anon` quand l'app se croit connectée. Voir
// `src/lib/auth/sessionGuardedFetch.ts` pour le pourquoi et l'incident de
// production du 2026-08-23.
//
// Les modules importés ici n'importent RIEN eux-mêmes : c'est ce qui
// empêche le cycle. Le renouvellement, lui, est enregistré au démarrage par
// AuthContext (`runRegisteredRefresher`).
const guardedFetch = createSessionGuardedFetch({
  anonKey: SUPABASE_PUBLISHABLE_KEY,
  // Chaque requête porte la version de l'app (X-Client-Info), voir
  // src/lib/clientVersion/signature.ts.
  baseFetch: withClientSignature((input, init) => fetch(input, init)),
  isSessionExpected,
  isAnonymousDataRoute: () => isAnonymousDataPath(globalThis.location?.pathname ?? '/'),
  isLogoutInProgress,
  refreshSession: runRegisteredRefresher,
});

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: hybridAuthStorage,
    persistSession: true,
    autoRefreshToken: true,
    flowType: 'pkce',
  },
  global: { fetch: guardedFetch },
});
